/**
 * Pool of `HTMLVideoElement`s for realtime preview, one per active layer instance (layer key).
 *
 * Elements are muted (audio is mixed by WebAudio) and slaved to the timeline clock: while playing
 * they run freely with small playback-rate corrections; while paused they are seeked precisely,
 * with seeks coalesced during scrubbing so the decoder is never flooded.
 */
import type { MediaInput } from '../types.ts';
import { inputKey, ObjectUrls } from './input.ts';
import { needsSeek, playbackCorrection, seekTarget } from './sync.ts';

interface Entry {
  key: string;
  el: HTMLVideoElement;
  /** Current source and its `inputKey`. */
  input: MediaInput;
  inputKey: string;
  /** Media item this element shows (for `invalidateMedia`). */
  mediaId: string | undefined;
  /** Latest desired media time. */
  wanted: number;
  mediaFps: number;
  duration: number | undefined;
  lastUsed: number;
}

export interface VideoSyncParams {
  playing: boolean;
  /** Media item being shown; lets `invalidateMedia` find its elements. */
  mediaId?: string;
  /** Timeline frame rate (drift tolerances are expressed in timeline frames). */
  fps: number;
  /** Clip speed. */
  rate: number;
  mediaFps?: number;
  duration?: number;
}

const MAX_ELEMENTS = 16;
const IDLE_RELEASE_MS = 4000;

export class VideoPool {
  private entries = new Map<string, Entry>();
  private readonly objectUrls = new ObjectUrls();

  /** `onFrame` is called when a paused element has a new frame to show (after seek/load). */
  constructor(private readonly onFrame: () => void) {}

  /** Ensures an element exists for `key` and brings it in sync with `time`. */
  sync(key: string, input: MediaInput, time: number, params: VideoSyncParams): void {
    const entry = this.acquire(key, input, params);
    entry.wanted = time;
    entry.lastUsed = performance.now();
    const el = entry.el;
    if (el.readyState < HTMLMediaElement.HAVE_METADATA) return; // 'loadedmetadata' will seek

    const atEnd = entry.duration !== undefined && time >= entry.duration - 1 / entry.mediaFps;
    if (params.playing && !atEnd) {
      if (el.paused) {
        if (needsSeek(el.currentTime, time, false, params.fps)) this.seek(entry, time);
        el.playbackRate = params.rate;
        void el.play().catch(() => undefined);
        return;
      }
      if (el.seeking) return;
      const corr = playbackCorrection(el.currentTime, time, params.fps, params.rate);
      if (corr.seek) this.seek(entry, time);
      if (Math.abs(el.playbackRate - corr.rate) > 1e-3) el.playbackRate = corr.rate;
    } else {
      if (!el.paused) el.pause();
      if (!el.seeking && needsSeek(el.currentTime, time, false, params.fps)) this.seek(entry, time);
    }
  }

  /** Pre-rolls an element for an upcoming layer: created, loaded and seeked, but not playing. */
  prepare(key: string, input: MediaInput, time: number, params: VideoSyncParams): void {
    this.sync(key, input, time, { ...params, playing: false });
  }

  /** The element for a layer if it has a decodable current frame. */
  frame(key: string): HTMLVideoElement | null {
    const e = this.entries.get(key);
    if (!e || e.el.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || e.el.videoWidth === 0) return null;
    return e.el;
  }

  /** The source the layer's element is showing (may lag behind the wanted one while switching). */
  input(key: string): MediaInput | undefined {
    return this.entries.get(key)?.input;
  }

  pauseAll(): void {
    for (const e of this.entries.values()) if (!e.el.paused) e.el.pause();
  }

  /** Releases elements not used recently (keeps those in `active`). */
  sweep(active: ReadonlySet<string>, now = performance.now()): void {
    for (const e of this.entries.values()) {
      if (!active.has(e.key) && now - e.lastUsed > IDLE_RELEASE_MS) this.release(e);
    }
  }

  /** Drops all elements whose source matches (by `inputKey`). */
  invalidate(predicate: (inputKey: string) => boolean): void {
    for (const e of [...this.entries.values()]) if (predicate(e.inputKey)) this.release(e);
  }

  /** Drops all elements showing a media item (e.g. its file or proxy changed). */
  invalidateMedia(mediaId: string): void {
    for (const e of [...this.entries.values()]) if (e.mediaId === mediaId) this.release(e);
  }

  dispose(): void {
    for (const e of [...this.entries.values()]) this.release(e);
    this.objectUrls.dispose();
  }

  // -------------------------------------------------------------------------------------------

  private acquire(key: string, input: MediaInput, params: VideoSyncParams): Entry {
    const ik = inputKey(input);
    let entry = this.entries.get(key);
    if (entry && entry.inputKey !== ik) {
      // Source switched (e.g. proxy became available): keep position, swap src.
      const previous = entry.input;
      entry.input = input;
      entry.inputKey = ik;
      entry.el.src = this.objectUrls.acquire(input);
      this.objectUrls.release(previous);
      entry.el.load();
    }
    if (!entry) {
      if (this.entries.size >= MAX_ELEMENTS) this.evictOldest();
      const el = document.createElement('video');
      el.muted = true;
      el.playsInline = true;
      el.preload = 'auto';
      el.crossOrigin = 'anonymous';
      el.disablePictureInPicture = true;
      const created: Entry = {
        key,
        el,
        input,
        inputKey: ik,
        mediaId: params.mediaId,
        wanted: 0,
        mediaFps: params.mediaFps ?? 30,
        duration: params.duration,
        lastUsed: performance.now(),
      };
      el.addEventListener('loadedmetadata', () => this.seek(created, created.wanted));
      el.addEventListener('seeked', () => {
        if (el.paused && needsSeek(el.currentTime, created.wanted, false, created.mediaFps)) {
          this.seek(created, created.wanted);
        } else {
          this.onFrame();
        }
      });
      el.addEventListener('loadeddata', () => this.onFrame());
      el.src = this.objectUrls.acquire(input);
      entry = created;
      this.entries.set(key, entry);
    }
    entry.mediaId = params.mediaId ?? entry.mediaId;
    entry.mediaFps = params.mediaFps ?? entry.mediaFps;
    entry.duration = params.duration ?? entry.duration;
    return entry;
  }

  private seek(entry: Entry, time: number): void {
    const el = entry.el;
    if (el.readyState < HTMLMediaElement.HAVE_METADATA) return;
    const duration = Number.isFinite(el.duration) ? el.duration : entry.duration;
    el.currentTime = seekTarget(time, entry.mediaFps, duration);
  }

  private evictOldest(): void {
    let oldest: Entry | null = null;
    for (const e of this.entries.values()) if (!oldest || e.lastUsed < oldest.lastUsed) oldest = e;
    if (oldest) this.release(oldest);
  }

  private release(entry: Entry): void {
    const el = entry.el;
    el.pause();
    el.removeAttribute('src');
    el.load(); // frees the decoder
    this.objectUrls.release(entry.input);
    this.entries.delete(entry.key);
  }
}
